import { useState, useEffect, useCallback } from 'react';
import { 
  collection, 
  query, 
  orderBy, 
  onSnapshot, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase/firebase';
import { SavedPlayer, CreateSavedPlayerData, UpdateSavedPlayerData } from '@shared/types';
import { normalizePhone, matchesPartialPhone, isValidPhoneSearchQuery } from '@shared/utils/phoneUtils';

export interface UseSavedPlayersResult {
  savedPlayers: SavedPlayer[];
  loading: boolean;
  error: Error | null;
  // Search functionality
  searchResults: SavedPlayer[];
  searchByName: (query: string) => void;
  searchByPhone: (query: string) => void;
  searchByNameOrPhone: (query: string) => void;
  clearSearch: () => void;
  // CRUD operations
  savePlayer: (data: CreateSavedPlayerData) => Promise<SavedPlayer | null>;
  updatePlayer: (playerId: string, data: UpdateSavedPlayerData) => Promise<void>;
  deletePlayer: (playerId: string) => Promise<void>;
  // Utility
  isPlayerSaved: (name: string, phone?: string) => boolean;
  findExistingPlayer: (name: string, phone?: string) => SavedPlayer | undefined;
}

const MIN_NAME_SEARCH_LENGTH = 2;

export function useSavedPlayers(roomId: string | null): UseSavedPlayersResult {
  const [savedPlayers, setSavedPlayers] = useState<SavedPlayer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [searchResults, setSearchResults] = useState<SavedPlayer[]>([]);

  // Subscribe to saved players collection for this room
  useEffect(() => {
    if (!roomId || !db) {
      setSavedPlayers([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const savedPlayersRef = collection(db, 'rooms', roomId, 'savedPlayers');
    const savedPlayersQuery = query(savedPlayersRef, orderBy('name', 'asc'));

    const unsubscribe = onSnapshot(
      savedPlayersQuery,
      (snapshot) => {
        const players = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        })) as SavedPlayer[];
        setSavedPlayers(players);
        setLoading(false);
      },
      (err) => {
        console.error('Error loading saved players:', err);
        setError(err as Error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [roomId]);

  // Search by name (case-insensitive contains match)
  const searchByName = useCallback((query: string) => {
    if (!query || query.length < MIN_NAME_SEARCH_LENGTH) {
      setSearchResults([]);
      return;
    }

    const lowerQuery = query.toLowerCase();
    const results = savedPlayers
      .filter(player => player.name.toLowerCase().includes(lowerQuery))
      .slice(0, 5); // Limit to 5 results

    setSearchResults(results);
  }, [savedPlayers]);

  // Search by phone (partial match, 5+ digits)
  const searchByPhone = useCallback((query: string) => {
    if (!isValidPhoneSearchQuery(query)) {
      setSearchResults([]);
      return;
    }

    const results = savedPlayers
      .filter(player => matchesPartialPhone(player.phoneNormalized, query))
      .slice(0, 5); // Limit to 5 results

    setSearchResults(results);
  }, [savedPlayers]);

  // Search by either name or phone (useful for combined autocomplete)
  const searchByNameOrPhone = useCallback((query: string) => {
    if (!query) {
      setSearchResults([]);
      return;
    }

    const normalizedQuery = normalizePhone(query);
    const lowerQuery = query.toLowerCase();
    
    // Determine if this looks more like a phone number (mostly digits)
    const isPhoneLike = normalizedQuery.length >= query.replace(/\s/g, '').length * 0.6;
    
    let results: SavedPlayer[] = [];

    if (isPhoneLike && isValidPhoneSearchQuery(query)) {
      // Search by phone
      results = savedPlayers.filter(player => 
        matchesPartialPhone(player.phoneNormalized, query)
      );
    }
    
    // Also search by name if we don't have enough phone results or query is text-like
    if (results.length < 5 && query.length >= MIN_NAME_SEARCH_LENGTH) {
      const nameResults = savedPlayers.filter(player => 
        player.name.toLowerCase().includes(lowerQuery) &&
        !results.some(r => r.id === player.id) // Avoid duplicates
      );
      results = [...results, ...nameResults];
    }

    setSearchResults(results.slice(0, 5));
  }, [savedPlayers]);

  // Clear search results
  const clearSearch = useCallback(() => {
    setSearchResults([]);
  }, []);

  // Save a new player
  const savePlayer = useCallback(async (data: CreateSavedPlayerData): Promise<SavedPlayer | null> => {
    if (!roomId || !db) {
      throw new Error('Room ID is required to save a player');
    }

    try {
      const savedPlayersRef = collection(db, 'rooms', roomId, 'savedPlayers');
      
      // Build the document data - only include phone fields if phone is provided
      // (Firestore doesn't accept undefined values)
      const docData: Record<string, any> = {
        name: data.name.trim(),
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
      };
      
      // Only add phone fields if phone is provided
      if (data.phone?.trim()) {
        docData.phone = data.phone.trim();
        docData.phoneNormalized = normalizePhone(data.phone);
      }
      
      const docRef = await addDoc(savedPlayersRef, docData);

      // Return the created player (with approximate data since we don't have the server timestamp yet)
      const savedPlayer: SavedPlayer = {
        id: docRef.id,
        name: data.name.trim(),
        created_at: new Date() as any,
        updated_at: new Date() as any,
      };
      if (data.phone?.trim()) {
        savedPlayer.phone = data.phone.trim();
        savedPlayer.phoneNormalized = normalizePhone(data.phone);
      }
      return savedPlayer;
    } catch (err) {
      console.error('Error saving player:', err);
      throw err;
    }
  }, [roomId]);

  // Update an existing player
  const updatePlayer = useCallback(async (playerId: string, data: UpdateSavedPlayerData): Promise<void> => {
    if (!roomId || !db) {
      throw new Error('Room ID is required to update a player');
    }

    try {
      const playerRef = doc(db, 'rooms', roomId, 'savedPlayers', playerId);
      
      const updateData: Record<string, any> = {
        updated_at: serverTimestamp(),
      };

      if (data.name !== undefined) {
        updateData.name = data.name.trim();
      }

      if (data.phone !== undefined) {
        updateData.phone = data.phone?.trim() || null;
        updateData.phoneNormalized = data.phone ? normalizePhone(data.phone) : null;
      }

      await updateDoc(playerRef, updateData);
    } catch (err) {
      console.error('Error updating player:', err);
      throw err;
    }
  }, [roomId]);

  // Delete a player
  const deletePlayer = useCallback(async (playerId: string): Promise<void> => {
    if (!roomId || !db) {
      throw new Error('Room ID is required to delete a player');
    }

    try {
      const playerRef = doc(db, 'rooms', roomId, 'savedPlayers', playerId);
      await deleteDoc(playerRef);
    } catch (err) {
      console.error('Error deleting player:', err);
      throw err;
    }
  }, [roomId]);

  // Check if a player is already saved (by exact name OR phone match)
  const isPlayerSaved = useCallback((name: string, phone?: string): boolean => {
    const normalizedName = name.trim().toLowerCase();
    const normalizedPhone = phone ? normalizePhone(phone) : '';

    return savedPlayers.some(player => {
      // Match by phone if both have phone numbers
      if (normalizedPhone && player.phoneNormalized) {
        return player.phoneNormalized === normalizedPhone;
      }
      // Match by exact name if no phone match
      return player.name.toLowerCase() === normalizedName;
    });
  }, [savedPlayers]);

  // Find existing player by name or phone
  const findExistingPlayer = useCallback((name: string, phone?: string): SavedPlayer | undefined => {
    const normalizedName = name.trim().toLowerCase();
    const normalizedPhone = phone ? normalizePhone(phone) : '';

    return savedPlayers.find(player => {
      // Match by phone if both have phone numbers
      if (normalizedPhone && player.phoneNormalized) {
        return player.phoneNormalized === normalizedPhone;
      }
      // Match by exact name if no phone match
      return player.name.toLowerCase() === normalizedName;
    });
  }, [savedPlayers]);

  return {
    savedPlayers,
    loading,
    error,
    searchResults,
    searchByName,
    searchByPhone,
    searchByNameOrPhone,
    clearSearch,
    savePlayer,
    updatePlayer,
    deletePlayer,
    isPlayerSaved,
    findExistingPlayer,
  };
}

